import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import {
  ChecklistQuestion,
  RadioQuestion,
  SubjectiveQuestion,
} from "../../../components/questionnaire";
import { PharmaBranchedQuestionnaire } from "../../../data-model/cmmMedicalNeccessityQuestionnaire";
import { RootState } from "../../../redux/store/store";
import { usePharmaQuestionnaireState } from "../hooks/usePharmaQuestionnaireState";
import NoQuestionsFound from "./noQuestionsFound";

interface PharmaQuestionsWithAllBranchingProps {
  /**
   * Notified whenever the questionnaire's overall validity changes.
   * The parent receives `true` when every visible question passes the
   * three validation rules in `validateQuestionnaireAnswers`.
   */
  onValidityChange?: (isValid: boolean) => void;
  /**
   * Notified whenever the in-memory workflow document changes (i.e. the
   * user edits an answer). The parent should treat this as the source
   * of truth for submission payloads, since the questionnaire prop is
   * only the initial server-fetched document.
   */
  onWorkflowDocumentChange?: (document: PharmaBranchedQuestionnaire) => void;
  questionnaire: PharmaBranchedQuestionnaire;
  initialQuestionnaire: PharmaBranchedQuestionnaire;
}

/**
 * Dummy page that loads the local example CMM workflow document, drives
 * it through {@link usePharmaQuestionnaireState}, and renders each
 * question with the appropriate questionnaire wrapper. Useful for
 * visually verifying the renderer pipeline end-to-end.
 *
 * @param props.onValidityChange - Optional listener for overall validity
 */
const PharmaQuestionsWithAllBranching: React.FC<
  PharmaQuestionsWithAllBranchingProps
> = ({
  onValidityChange,
  onWorkflowDocumentChange,
  questionnaire,
  initialQuestionnaire,
}) => {
  const { user } = useSelector(
    (state: RootState) => state.firebaseAuthentication,
  );

  const {
    visibleQuestions,
    isQuestionnaireValid,
    updatedAnswerQuestionNames,
    workflowDocument,
    handlePatch,
    handleSubmit,
    handleCancel,
  } = usePharmaQuestionnaireState(
    questionnaire,
    initialQuestionnaire,
    user?.email ?? "",
  );

  useEffect(() => {
    onValidityChange?.(isQuestionnaireValid);
  }, [isQuestionnaireValid, onValidityChange]);

  useEffect(() => {
    onWorkflowDocumentChange?.(workflowDocument);
  }, [workflowDocument, onWorkflowDocumentChange]);

  return (
    <div className="box-border flex h-full flex-col gap-4 overflow-y-auto bg-white p-4">
      {visibleQuestions.length > 0 ? (
        visibleQuestions.map((q, i) => {
          const common = {
            question: q,
            stepNumber: i + 1,
            onChange: handlePatch,
            evidenceCards: q.evidenceCards,
            confidenceScore: q.confidenceScore,
            answerChangedReason: q.answerChangedReason,
            shouldShowUserFeedBack: updatedAnswerQuestionNames.has(q.name),
            onSubmit: handleSubmit,
            onCancel: handleCancel,
          };
          if (q.question_type === "subjective") {
            return <SubjectiveQuestion key={q.name || i} {...common} />;
          }
          if (q.question_type === "radio-subjective") {
            return <RadioQuestion key={q.name || i} {...common} />;
          }
          return <ChecklistQuestion key={q.name || i} {...common} />;
        })
      ) : (
        <NoQuestionsFound />
      )}
    </div>
  );
};

export default PharmaQuestionsWithAllBranching;
